import type { Permission } from '@/features/users-roles/constants';
import type { NavItem, NavSubItem } from './app-sidebar';

/**
 * Permission-filters the sidebar tree: leaf items hide when their check
 * fails; sections and groups hide when all their children are hidden.
 * Extracted from AppSidebar so the visibility rules are unit-testable.
 */
export function filterNavItems(
    routes: NavItem[],
    hasPermission: (permission: Permission) => boolean,
    hasAnyPermission: (permissions: Permission[]) => boolean,
): NavItem[] {
    /**
     * A section child is either a leaf (judged on its own permissions) or a
     * nested group, which carries none of its own and survives only while at
     * least one of its children does — otherwise an empty parent row would be
     * left folding nothing.
     */
    const keepChild = (item: NavSubItem): NavSubItem | null => {
        if (item.items) {
            const items = item.items.filter((leaf) => isLeafVisible(leaf));
            return items.length === 0 ? null : { ...item, items };
        }
        return isLeafVisible(item) ? item : null;
    };

    const isLeafVisible = (item: NavSubItem): boolean => {
        if (item.requiredPermission && !hasPermission(item.requiredPermission)) return false;
        if (item.anyOfPermissions && !hasAnyPermission(item.anyOfPermissions)) return false;
        return true;
    };

    return routes
        .map((route): NavItem | null => {
            if (route.items) {
                const items = route.items
                    .map(keepChild)
                    .filter((item): item is NavSubItem => item !== null);
                if (items.length === 0) return null;
                return { ...route, items };
            }
            if (route.requiredPermission && !hasPermission(route.requiredPermission)) {
                return null;
            }
            if (route.anyOfPermissions && !hasAnyPermission(route.anyOfPermissions)) {
                return null;
            }
            return route;
        })
        .filter((route): route is NavItem => route !== null);
}
